import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormFieldComponent } from '@pishgamrayan/ng-form-kit';
import { finalize, map } from 'rxjs';

import { BaseLayerSearchService } from 'src/app/core/api-gis/services/base-layer-search.service';
import { MapService } from 'src/app/core/api/services';
import { TranslationModule } from 'src/app/i18n';
import { UserLayerService } from '../../../core/api-gis/services/user-layer.service';
import { MaterialModule } from '../../modules/material/material.module';
import { BaseComponentComponent } from '../base/base-component/base-component.component';
import { CustomSelectSearchComponent } from '../select-search/custom-select-search.component';
import { MapPlacesGridSelectionListModule } from '../selection-list/map-places-grid-selection-list/map-places-grid-selection-list.module';
import { MapPlacesGridService } from './map-places-grid.service';
import { PlaceGridLayerData } from './place-grid-layer-data.model';
import { PlaceGridLayerIconDirective } from './place-grid-layer-icon.directive';
import { PlaceGridLayer } from './place-grid-layer.model';
@Component({
  selector: 'map-places-grid',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    TranslationModule,
    MatTooltipModule,
    PlaceGridLayerIconDirective,
    CustomSelectSearchComponent,
    MapPlacesGridSelectionListModule,
    FormFieldComponent,
  ],
  templateUrl: './map-places-grid.component.html',
  styleUrls: ['./map-places-grid.component.scss'],
})
export class MapPlacesGridComponent
  extends BaseComponentComponent
  implements OnInit
{
  //#region
  #mapPlacesGridService = inject(MapPlacesGridService);
  layers: PlaceGridLayer[] = [];
  lastSelectedLayer: string = '';
  layerDataOptions: PlaceGridLayerData[] = [];
  selectedLayers: string[] = [];
  #selectedPlace: PlaceGridLayerData = {};
  isShowFilters: boolean = true;
  provinces: { id?: any; name?: any }[] = [];
  cities: { id?: any; name?: any }[] = [];
  selectedProvince: string = '';
  selectedCity: string = '';
  searchTerm: string = '';
  form: FormGroup;
  lat: number = 0;
  lon: number = 0;
  distance: number = 1000;
  size: number = 100;
  cityLoading: boolean = false;

  @Output() selectedPlace: EventEmitter<PlaceGridLayerData> =
    new EventEmitter<PlaceGridLayerData>();
  @Output() layerData: EventEmitter<PlaceGridLayerData[]> = new EventEmitter<
    PlaceGridLayerData[]
  >();

  //#endregion

  constructor(
    private userLayerService: UserLayerService,
    private baseLayerService: BaseLayerSearchService,
    private mapService: MapService
  ) {
    super();
  }

  ngOnInit(): void {
    this.getLayersApi();
    this.getProvincesApi();
    this.initForm();
  }

  initForm() {
    this.form = new FormGroup({
      lat: new FormControl(''),
      lon: new FormControl(''),
      searchTerm: new FormControl(''),
    });
  }

  //#region api

  getLayersApi() {
    if (this.#mapPlacesGridService.layers?.length) {
      this.layers = this.#mapPlacesGridService.layers;
      return;
    }
    this.userLayerService
      .gisUserLayerGetLayersInfoGet()
      .pipe(this.basePipeOperators())
      .pipe(map((res) => JSON.parse(res as any)))
      .subscribe((res) => {
        this.layers = this.hasAccessToLayerApi(res);
        this.#mapPlacesGridService.layers = this.layers;
      });
  }

  getLayerDataApi() {
    const body: {
      searchTerm?: string;
      provinceName?: string;
      cityName?: string;
      type?: string;
      lat?: number;
      lon?: number;
      userId?: number;
      zoomLevel?: number;
    } = {};

    if (+this.lat && +this.lat != 0) body.lat = +this.lat;
    if (+this.lon && +this.lon != 0) body.lon = +this.lon;

    body.provinceName = this.selectedProvince || '';
    body.cityName = this.selectedCity || '';
    body.searchTerm = this.searchTerm || '';
    body.type = this.lastSelectedLayer || '';

    this.userLayerService
      .gisUserLayerSearchPost({
        body,
      })
      .pipe(this.basePipeOperators())
      .pipe(map((res) => JSON.parse(res as any)))
      .subscribe((res) => {
        this.removeLayerDataByProvince();
        this.layerDataOptions = res;
        this.layerData.emit(this.layerDataOptions);
      });
  }

  getProvincesApi() {
    if (this.#mapPlacesGridService.provinces?.length) {
      this.provinces = this.#mapPlacesGridService.provinces;
      return;
    }
    this.userLayerService
      .gisUserLayerGetProvincesGet()
      .pipe(this.basePipeOperators())
      .pipe(map((res) => JSON.parse(res as any)))
      .subscribe((res: any[]) => {
        this.provinces = res.map((item) => ({ id: item, name: item }));
        this.#mapPlacesGridService.provinces = this.provinces;
      });
  }

  getCityApi(province) {
    this.cityLoading = true;
    this.userLayerService
      .gisUserLayerGetCitiesGet({
        province,
      })
      .pipe(
        this.basePipeOperators(),
        finalize(() => {
          this.cityLoading = false;
        })
      )
      .pipe(map((res) => JSON.parse(res as any)))
      .subscribe((res: any[]) => {
        this.cities = res.map((item) => ({ id: item, name: item }));
      });
  }

  hasAccessToLayerApi(layers: PlaceGridLayer[]): PlaceGridLayer[] {
    let visibleLayers: PlaceGridLayer[] = [];

    layers.forEach((layer) => {
      switch (layer.icon) {
        case 'fa-gamepad-modern':
          this.mapService
            .mapNationalComputerGamesFoundation()
            .pipe(this.basePipeOperators())
            .subscribe((res) => {
              visibleLayers.push(layer);
            });
          break;
        case 'fa-place-of-worship':
          this.mapService
            .mapBlessedTombs()
            .pipe(this.basePipeOperators())
            .subscribe((res) => {
              visibleLayers.push(layer);
            });
          break;
        case 'fa-duotone fa-book-circle-arrow-up':
          this.mapService
            .mapPublishers()
            .pipe(this.basePipeOperators())
            .subscribe((res) => {
              visibleLayers.push(layer);
            });
          break;
        case 'fa-theater-masks':
          this.mapService
            .mapCulturalCenters()
            .pipe(this.basePipeOperators())
            .subscribe((res) => {
              visibleLayers.push(layer);
            });
          break;
        case 'fa-hand-holding-seedling':
          this.mapService
            .mapMosques()
            .pipe(this.basePipeOperators())
            .subscribe((res) => {
              visibleLayers.push(layer);
            });
          break;
        case 'fa-camera-movie':
          this.mapService
            .mapCinema()
            .pipe(this.basePipeOperators())
            .subscribe((res) => {
              visibleLayers.push(layer);
            });
          break;
        case 'fa-solid fa-gavel':
          this.mapService
            .mapLegalDigitalCulturalInstitutions()
            .pipe(this.basePipeOperators())
            .subscribe((res) => {
              visibleLayers.push(layer);
            });
          break;
        case 'fa-book-quran':
          this.mapService
            .mapQuranicInstitutes()
            .pipe(this.basePipeOperators())
            .subscribe((res) => {
              visibleLayers.push(layer);
            });
          break;
        case 'fa-books':
          this.mapService
            .mapLibraries()
            .pipe(this.basePipeOperators())
            .subscribe((res) => {
              visibleLayers.push(layer);
            });
          break;
        default:
          break;
      }
    });

    return visibleLayers;
  }

  //#endregion

  onSelectLayer(layerName) {
    const index = this.selectedLayers.findIndex((item) => item == layerName);
    if (index == -1) {
      this.lastSelectedLayer = layerName;
      this.selectedLayers.push(layerName);
      this.getLayerDataApi();
    } else {
      this.selectedLayers.splice(index, 1);
      this.removeLayerDataByLayerName(layerName);
      if (layerName == this.lastSelectedLayer) this.lastSelectedLayer = '';
      if (!this.selectedLayers.length) {
        this.layerData.emit([]);
        this.lastSelectedLayer = '';
      }
      return;
    }
  }

  removeLayerDataByLayerName(layerName) {
    // this.layerDataOptions = this.layerDataOptions.filter(
    //   (item) => item.type !== layerName
    // );
    this.layerData.emit(this.layerDataOptions);
  }

  removeLayerDataByProvince() {
    // if (!this.selectedProvince) return;
    // this.layerDataOptions = this.layerDataOptions.filter(
    //   (item) => item.parents.Province == this.selectedProvince
    // );
    this.layerData.emit(this.layerDataOptions);
  }

  onSelectPlace(event: any) {
    this.#selectedPlace = event;
    this.selectedPlace.emit(this.#selectedPlace);
  }

  onChangeProvince(event) {
    this.selectedProvince = event;
    this.getCityApi(this.selectedProvince);
  }

  onFilterToggle() {
    this.isShowFilters = !this.isShowFilters;
    if (this.isShowFilters && !this.selectedProvince) {
      this.selectedLayers = [];
      this.lastSelectedLayer = '';
      this.layerData.emit([]);
      this.layerDataOptions = [];
    }
  }

  onRemoveSelectedLayer() {
    this.selectedLayers = [];
    this.layerDataOptions = [];
    this.lastSelectedLayer = '';
    this.layerData.emit(this.layerDataOptions);
    this.selectedPlace.emit();
  }
}
